import { Decimal } from "decimal.js";
import { Commodities, CommodityOptions } from "@/lib/api/commodity";
import { safeParseDecimal } from "@/lib/api/common";
import { getPath } from "@/lib/prelude";
import { ContractTypeOptions } from "@/lib/api/otc";
import { getRiskHexColor } from "./colorPalette";


export interface RevenueField {
  quantity: Decimal;
  value: Decimal;
  per_acre: Decimal;
}

export interface RiskRevenueSummaryProps {
  // Commodity, then Customer Id, then breakdown
  revenue_breakdown: Record<string, Record<string, RevenueBreakdownTypes>>;
  className?: string;
}

export interface CostOfProduction {
  cost_per_acre: Decimal;
  acreage: Decimal;

  projected_yield: Decimal;
  actual_yield: Decimal;
  total_yield: Decimal;
  total_production: Decimal;
  total_cost: Decimal;
}

export interface RevenueBreakdownTypes {
  sold: {
    revenue: RevenueField
    total: { revenue: RevenueField };
  };
  unknown: {
    total: { revenue: RevenueField };
  };
  unsold: {
    futures: { revenue: RevenueField };
    basis: { revenue: RevenueField };
    unset: { revenue: RevenueField };
    total: { revenue: RevenueField };

    // Unsold breakdown portion
    unsold_hta: { revenue: RevenueField };
    unsold_otc: { revenue: RevenueField };
    unsold_basis: { revenue: RevenueField };
    unsold_other: { revenue: RevenueField };
    unsold_unprotected: { revenue: RevenueField };
  };
  brokerage: {
    unrealized: RevenueField;
    realized: RevenueField;
    total: { revenue: RevenueField };
  };
  // Breakout type or NonBreakout
  cost_of_production: {
    Base: CostOfProduction
  };
}

export function formatPercent(num: number) {
  return `${num.toFixed(1)}%`;
};

export function get_customer_commodity_top_level({ commodity_type, customer_id, summaryData, skipProfileCheck }: { customer_id: string; commodity_type: Commodities; summaryData: any; skipProfileCheck?: boolean }) {
  if (!skipProfileCheck && (!summaryData.customer_commodity_stats || !summaryData.customer_commodity_stats[commodity_type] || !summaryData.customer_commodity_stats[commodity_type][customer_id])) {
    return null;
  }

  const profile_stats = getPath(summaryData, ["customer_commodity_stats", commodity_type, customer_id], {}) || {};
  const reduceQuantity = (records: Record<string, { quantity: Decimal }>) => Object.values(records).reduce((acc, curr) => acc.plus(curr.quantity || 0), new Decimal(0));

  // NOTE: We have custom types that differ from the DB types, as they can be location specific.
  const hta_stats: Decimal = safeParseDecimal({})(getPath(summaryData.customer_commodity_general_stats, [commodity_type, customer_id, 'HTA', 'quantity'], new Decimal(0)) as Decimal);
  const beacon_stats: Decimal = safeParseDecimal({})(getPath(summaryData.customer_commodity_general_stats, [commodity_type, customer_id, 'Beacon', 'quantity'], new Decimal(0)) as Decimal);
  const applicable_dp_quantity: Decimal = safeParseDecimal({})(getPath(summaryData.customer_commodity_general_stats, [commodity_type, customer_id, 'Applicable DP', 'quantity'], new Decimal(0)) as Decimal);

  const dpq_stats: Record<string, { quantity: Decimal }> = getPath(summaryData.customer_commodity_dpq_stats, [commodity_type, customer_id], {}) as any;
  const direct_sale_stats: Record<string, { quantity: Decimal }> = getPath(summaryData.customer_commodity_direct_sale_stats, [commodity_type, customer_id], {}) as any;

  const dpq_starting_quantity = safeParseDecimal({})(dpq_stats["DPQ"]?.quantity as any);
  const dpq_basis_quantity = safeParseDecimal({})(dpq_stats["DPQ Basis"]?.quantity as any);
  const dpq_futures_quantity = safeParseDecimal({})(dpq_stats["DPQ Futures"]?.quantity as any);

  const direct_sales_starting_quantity = safeParseDecimal({})(direct_sale_stats["DirectSale"]?.quantity as any);
  const direct_sales_basis_quantity = safeParseDecimal({})(direct_sale_stats["DirectSale Basis"]?.quantity as any);
  const direct_sales_futures_quantity = safeParseDecimal({})(direct_sale_stats["DirectSale Futures"]?.quantity as any);

  const direct_sales_all_futures_quantity = direct_sales_futures_quantity.plus(direct_sales_starting_quantity);

  // NOTE: Andrew(2025-08-18): DPQ Futures shows up in DPQ Futures and Non applicable DP.
  const non_applicable_dp_quantity = safeParseDecimal({})(getPath(summaryData.customer_commodity_general_stats, [commodity_type, customer_id, 'Non Applicable DP', 'quantity'], new Decimal(0)) as Decimal).plus(dpq_starting_quantity);

  const cash_stats: Decimal = safeParseDecimal({})(getPath(summaryData.customer_commodity_cash_stats, [commodity_type, customer_id, 'quantity'], new Decimal(0)) as Decimal);
  const open_offer_map_stats: Record<string, { contract_type?: string; source_type: string; quantity: Decimal }> = getPath(summaryData.customer_commodity_open_offers, [commodity_type, customer_id], {}) as any;
  const otc_map_stats: Record<string, { quantity: Decimal }> = getPath(summaryData.customer_commodity_otc_stats, [commodity_type, customer_id], {}) as Record<string, { quantity: Decimal }>;
  const double_up_map_stats: Record<string, { quantity: Decimal }> = getPath(summaryData.customer_commodity_double_up_stats, [commodity_type, customer_id], {}) as Record<string, { quantity: Decimal }>;

  const otc_futures_map_stats: Record<string, { quantity: Decimal }> = getPath(summaryData.customer_commodity_otc_futures_stats, [commodity_type, customer_id], {}) as Record<string, { quantity: Decimal }>;
  const otc_basis_map_stats: Record<string, { quantity: Decimal }> = getPath(summaryData.customer_commodity_otc_basis_stats, [commodity_type, customer_id], {}) as Record<string, { quantity: Decimal }>;
  const beacon_basis_map_stats: Record<string, { quantity: Decimal }> = getPath(summaryData.customer_commodity_beacon_basis_stats, [commodity_type, customer_id], {}) as Record<string, { quantity: Decimal }>;
  const otc_stats: Decimal = Object.values(otc_map_stats).reduce((acc, curr) => acc.plus(curr.quantity || 0), new Decimal(0));

  const manual_open_offer_map_stats: Record<string, { manual_type?: string; quantity: Decimal }> = getPath(summaryData.customer_commodity_manual_open_offer_stats, [commodity_type, customer_id], {}) as Record<string, { quantity: Decimal }>;
  const manual_otc_offer_up_map_stats: Record<string, { quantity: Decimal }> = getPath(summaryData.customer_commodity_manual_otc_offer_up_stats, [commodity_type, customer_id], {}) as Record<string, { quantity: Decimal }>;
  const manual_open_offer_stats = Object.values(manual_open_offer_map_stats).reduce((acc, curr) => acc.plus(curr.quantity || 0), new Decimal(0));
  const manual_otc_offer_up_stats = Object.values(manual_otc_offer_up_map_stats).reduce((acc, curr) => acc.plus(curr.quantity || 0), new Decimal(0));
  const base_open_offer_stats = Object.values(open_offer_map_stats).reduce((acc, curr) => acc.plus(curr.quantity || 0), new Decimal(0));

  const open_offer_stats: Decimal = base_open_offer_stats.plus(manual_otc_offer_up_stats).plus(manual_open_offer_stats);

  const non_double_up_open_offers: Decimal = Object.values(open_offer_map_stats).reduce((acc, curr) => {
    if (!!/otc/i.test(curr.source_type || "")) { return acc; }
    return acc.plus(curr.quantity || 0);
  }, new Decimal(0)).plus(Object.values(manual_open_offer_map_stats).reduce((acc, curr) => {
    if (!!/otc/i.test(curr.manual_type || "")) { return acc; }
    return acc.plus(curr.quantity || 0);
  }, new Decimal(0)));

  const double_up_open_offers: Decimal = Object.values(open_offer_map_stats).reduce((acc, curr) => {
    if (!/otc/i.test(curr.source_type || "")) { return acc; }
    return acc.plus(curr.quantity || 0);
  }, new Decimal(0)).plus(manual_otc_offer_up_stats).plus(Object.values(manual_open_offer_map_stats).reduce((acc, curr) => {
    if (!/otc/i.test(curr.manual_type || "")) { return acc; }
    return acc.plus(curr.quantity || 0);
  }, new Decimal(0)));

  const otc_futures_stats: Decimal = Object.values(otc_futures_map_stats).reduce((acc, curr) => acc.plus(curr.quantity || 0), new Decimal(0));
  const otc_basis_stats: Decimal = Object.values(otc_basis_map_stats).reduce((acc, curr) => acc.plus(curr.quantity || 0), new Decimal(0));
  const beacon_basis_stats: Decimal = Object.values(beacon_basis_map_stats).reduce((acc, curr) => acc.plus(curr.quantity || 0), new Decimal(0));
  const open_brokerage_stats: Decimal = safeParseDecimal({})(getPath(summaryData.customer_commodity_open_brokerage_stats, [commodity_type, customer_id, 'quantity'], new Decimal(0)) as Decimal);
  const closed_brokerage_stats: Decimal = safeParseDecimal({})(getPath(summaryData.customer_commodity_closed_brokerage_stats, [commodity_type, customer_id, 'quantity'], new Decimal(0)) as Decimal);

  const non_applicable_open_brokerage_stats: Decimal = safeParseDecimal({})(getPath(summaryData.customer_commodity_non_applicable_open_brokerage_stats, [commodity_type, customer_id, 'quantity'], new Decimal(0)) as Decimal);
  const non_applicable_closed_brokerage_stats: Decimal = safeParseDecimal({})(getPath(summaryData.customer_commodity_non_applicable_closed_brokerage_stats, [commodity_type, customer_id, 'quantity'], new Decimal(0)) as Decimal);

  const cash_quantity = safeParseDecimal({})(getPath(summaryData.customer_commodity_cash_stats, [commodity_type, customer_id, 'quantity'], new Decimal(0)) as Decimal);
  const hta_quantity = safeParseDecimal({})(getPath(summaryData.customer_commodity_general_stats, [commodity_type, customer_id, 'HTA', 'quantity'], new Decimal(0)) as Decimal);
  const basis_quantity = safeParseDecimal({})(getPath(summaryData.customer_commodity_general_stats, [commodity_type, customer_id, 'Basis', 'quantity'], new Decimal(0)) as Decimal);
  const beacon_quantity = safeParseDecimal({})(getPath(summaryData.customer_commodity_general_stats, [commodity_type, customer_id, 'Beacon', 'quantity'], new Decimal(0)) as Decimal);

  const unknown_otc_quantity = safeParseDecimal({})(getPath(otc_map_stats, ['OTC', 'quantity'], new Decimal(0)) as Decimal);

  // NOTE: Percent Hedged is (Cash + HTA + Beacon + OTC) / Total Projected Production
  // Where OTC is the general OTC list
  const hedged_without_beacon_quantity = hta_stats.plus(cash_stats).plus(otc_stats).plus(open_brokerage_stats).plus(beacon_basis_stats);
  const hedged_without_beacon_quantity_with_dp = hedged_without_beacon_quantity.plus(applicable_dp_quantity);

  const projected_production = safeParseDecimal({})(profile_stats.projected_production || 0);
  const actual_production = safeParseDecimal({})(profile_stats.actual_production || 0);

  const projected_yield = safeParseDecimal({})(profile_stats.projected_yield || 0);
  const actual_yield = safeParseDecimal({})(profile_stats.actual_yield || 0);
  const total_yield = !actual_yield.isZero() ? actual_yield : projected_yield;

  const production = actual_production.isZero() ? projected_production : actual_production;
  const calculateProjectPercentage = (value: Decimal | number) => production.isZero() ? new Decimal(0) : safeParseDecimal({})(value).div(production).times(100);

  const hedged_without_beacon_percent = calculateProjectPercentage(hedged_without_beacon_quantity);
  const hedged_quantity = hedged_without_beacon_quantity.plus(beacon_stats);
  const otc_beacon_basis_quantity = safeParseDecimal({})(beacon_basis_stats.toNumber()).plus(safeParseDecimal({})(otc_basis_stats));

  const percent_hedged_with_dp = calculateProjectPercentage(hedged_without_beacon_quantity.plus(beacon_stats).plus(applicable_dp_quantity));

  const futuresBreakdown = [
    { label: 'HTA', id: 'hta', group: 'percent_hedged', value: hta_stats.toNumber(), color: getRiskHexColor('hta'), percent: calculateProjectPercentage(hta_stats).toNumber() },
    // { label: 'Beacon', id: 'beacon', group: 'beacon', value: beacon_stats.toNumber(), color: getRiskHexColor('beacon'), percent: calculateProjectPercentage(beacon_stats).toNumber() },
    // Make sure to include the implied OTC futures
    { label: 'OTC', id: 'otc', group: 'percent_hedged', value: otc_basis_stats.plus(otc_futures_stats).toNumber(), color: getRiskHexColor('otc'), percent: calculateProjectPercentage(otc_basis_stats.plus(otc_futures_stats)).toNumber() },
    { label: 'Brokerage', id: 'brokerage', group: 'percent_hedged', value: open_brokerage_stats.toNumber(), color: getRiskHexColor('brokerage'), percent: calculateProjectPercentage(open_brokerage_stats).toNumber() },
    { label: 'DPQ', id: 'dpq_futures', group: 'dp', value: dpq_futures_quantity.toNumber(), color: getRiskHexColor('dpq'), percent: calculateProjectPercentage(dpq_futures_quantity).toNumber() },
    { label: 'Cash', id: 'cash', group: 'percent_hedged', value: cash_stats.toNumber(), color: getRiskHexColor('cash'), percent: calculateProjectPercentage(cash_stats).toNumber() },

    { label: 'Double Up', id: 'double_up_id', group: 'open_offer', value: double_up_open_offers.toNumber(), color: getRiskHexColor('brokerage'), percent: calculateProjectPercentage(double_up_open_offers).toNumber() },
    { label: 'Open Orders', id: 'open_offer_id', group: 'open_offer', value: non_double_up_open_offers.toNumber(), color: getRiskHexColor('brokerage'), percent: calculateProjectPercentage(non_double_up_open_offers).toNumber() },

    { label: 'Direct', id: 'oddities_futures', group: 'percent_hedged', value: direct_sales_all_futures_quantity.toNumber(), color: getRiskHexColor('dpq'), percent: calculateProjectPercentage(direct_sales_all_futures_quantity).toNumber() },
  ];

  const physicalBreakdown = [
    { label: 'Basis', id: 'basis_physical', group: 'percent_hedged', value: basis_quantity.toNumber(), color: getRiskHexColor('basis'), percent: calculateProjectPercentage(basis_quantity).toNumber() },
    { label: 'OTC', id: 'otc_physical', group: 'percent_hedged', value: otc_basis_stats.toNumber(), color: getRiskHexColor('otc'), percent: calculateProjectPercentage(otc_basis_stats).toNumber() },
    // { label: 'Beacon', id: 'beacon_physical', group: 'beacon', value: beacon_basis_stats.toNumber(), color: getRiskHexColor('beacon'), percent: calculateProjectPercentage(beacon_basis_stats).toNumber() },
    { label: 'DPQ', id: 'dpq_physical', group: 'dp', value: dpq_basis_quantity.toNumber(), color: getRiskHexColor('dpq'), percent: calculateProjectPercentage(dpq_basis_quantity).toNumber() },
    { label: 'DPQ Unset', id: 'dpq_unset', group: 'dp', value: non_applicable_dp_quantity.toNumber(), color: getRiskHexColor('dpq'), percent: calculateProjectPercentage(non_applicable_dp_quantity).toNumber() },
    { label: 'Cash', id: 'cash_physical', group: 'percent_hedged', value: cash_stats.toNumber(), color: getRiskHexColor('cash'), percent: calculateProjectPercentage(cash_stats).toNumber() },
    { label: 'Direct', id: 'oddities_physical', group: 'percent_hedged', value: direct_sales_basis_quantity.toNumber(), color: getRiskHexColor('dpq'), percent: calculateProjectPercentage(direct_sales_basis_quantity).toNumber() },
  ];

  const futures_qty = futuresBreakdown.filter(item => ['percent_hedged', 'beacon'].includes(item.group)).reduce((acc, item) => acc.plus(item.value), new Decimal(0));
  const physical_qty = physicalBreakdown.filter(item => ['percent_hedged', 'beacon'].includes(item.group)).reduce((acc, item) => acc.plus(item.value), new Decimal(0));

  const percentHedgedWithBeaconBreakdowns = [{
    id: 'base',
    value: futuresBreakdown.filter(item => ['percent_hedged', 'beacon', 'dp'].includes(item.group)).reduce((acc, item) => acc.plus(item.value), new Decimal(0)),
  }, {
    id: 'base_with_open_offer',
    value: futuresBreakdown.filter(item => ['percent_hedged', 'beacon', 'dp', 'open_offer'].includes(item.group)).reduce((acc, item) => acc.plus(item.value), new Decimal(0)),
  }];

  const percentHedgedWithOutBeaconBreakdowns = [{
    id: 'base',
    value: futuresBreakdown.filter(item => ['percent_hedged', 'dp'].includes(item.group)).reduce((acc, item) => acc.plus(item.value), new Decimal(0)),
  }, {
    id: 'base_with_open_offer',
    value: futuresBreakdown.filter(item => ['percent_hedged', 'dp', 'open_offer'].includes(item.group)).reduce((acc, item) => acc.plus(item.value), new Decimal(0)),
  }];

  // NOTE: Base Percent Hedge Doesn include open offers because its harder to reason about how much is actually sold
  const percent_hedged_quantity = futuresBreakdown.filter(item => ['percent_hedged', 'beacon', 'dp'].includes(item.group)).reduce((acc, item) => acc.plus(item.value), new Decimal(0))
  const percent_physical_quantity = physicalBreakdown.filter(item => ['percent_hedged', 'beacon', 'dp'].includes(item.group)).reduce((acc, item) => acc.plus(item.value), new Decimal(0))

  const percent_hedged = calculateProjectPercentage(percent_hedged_quantity);
  const percent_physical = calculateProjectPercentage(percent_physical_quantity);

  // NOTE: Production Solid is just futures (Andrew 2025-08-28)
  const total_production_sold = percent_hedged_quantity;
  const total_production_unsold = production.minus(total_production_sold);

  const unprotected_qty = new Decimal(total_production_unsold);

  const unprotectedFutures = Decimal.max(production.minus(percent_hedged_quantity), 0);
  const unprotectedPhysical = Decimal.max(production.minus(percent_physical_quantity), 0);

  const unprotectedFuturesPercent = calculateProjectPercentage(unprotectedFutures);
  const unprotectedPhysicalPercent = calculateProjectPercentage(unprotectedPhysical);

  futuresBreakdown.push({ label: 'Unprotected', id: 'unprotected_future', group: 'unprotected', value: unprotectedFutures.toNumber(), color: getRiskHexColor('unprotected'), percent: unprotectedFuturesPercent.toNumber() });
  physicalBreakdown.push({ label: 'Unsold', id: 'unprotected_physical', group: 'unprotected', value: unprotectedPhysical.toNumber(), color: getRiskHexColor('unprotected'), percent: unprotectedPhysicalPercent.toNumber() });

  const double_up_quantity = Object.values(double_up_map_stats).reduce((acc, curr) => acc.plus(curr.quantity || 0), new Decimal(0));

  const base_revenue_data: RevenueBreakdownTypes = getPath(summaryData.revenue_breakdown, [commodity_type, customer_id], {}) as any;
  const computeMetaData = (item: { value: number; quantity: number; label: string; id: string; }) => ({
    ...item,
    per_acre: safeParseDecimal({})(item.value).div(base_revenue_data?.cost_of_production?.Base?.acreage || 1).toNumber(),
    per_uom: item.quantity !== 0 ? safeParseDecimal({})(item.value).div(item.quantity).toNumber() : 0,
    uom_label: commodity_type === "CNL" ? "lb" : "bu"
  });

  const unsold_revenue_data = [
    {
      label: "HTA/OTC Projected",
      id: 'hta_otc_projected',
      value: safeParseDecimal({})(base_revenue_data?.unsold?.unsold_hta?.revenue?.value ?? 0).plus(base_revenue_data?.unsold?.unsold_otc?.revenue?.value ?? 0).plus(base_revenue_data?.unsold?.unsold_other?.revenue?.value ?? 0).toNumber(),
      quantity: safeParseDecimal({})(base_revenue_data?.unsold?.unsold_hta?.revenue?.quantity ?? 0).plus(base_revenue_data?.unsold?.unsold_otc?.revenue?.quantity ?? 0).plus(base_revenue_data?.unsold?.unsold_other?.revenue?.quantity ?? 0).toNumber(),
      per_acre: 0,
      per_uom: 0,
    },
    {
      label: "Basis/Unprotected Proj",
      id: 'basis_unprotected_projected',
      value: safeParseDecimal({})(base_revenue_data?.unsold?.unsold_basis?.revenue?.value ?? 0)
        .plus(Decimal.max(
          (safeParseDecimal({})(0).plus(base_revenue_data?.unsold?.unsold_unprotected?.revenue?.value ?? 0).sub(base_revenue_data?.unsold?.unsold_basis?.revenue?.value ?? 0)),
          0
        )).toNumber(),
      quantity: safeParseDecimal({})(base_revenue_data?.unsold?.unsold_basis?.revenue?.quantity ?? 0)
        .plus(Decimal.max(
          safeParseDecimal({})(0).plus(base_revenue_data?.unsold?.unsold_unprotected?.revenue?.quantity ?? 0).sub(base_revenue_data?.unsold?.unsold_basis?.revenue?.quantity ?? 0),
          0
        )).toNumber(),
      per_acre: 0,
      per_uom: 0,
    }
  ].map(computeMetaData);

  const revenue_data: { value: number; quantity: number; label: string; id: string; per_acre: number; per_uom: number; uom_label: string; ignore_quantity_for_cop?: boolean  }[] = [
    {
      label: "Sold Revenue",
      id: 'sold_revenue',
      value: safeParseDecimal({})(base_revenue_data?.sold?.total?.revenue?.value || 0).toNumber(),
      quantity: safeParseDecimal({})(base_revenue_data?.sold?.total?.revenue?.quantity || 0).toNumber(),
      per_acre: 0,
      per_uom: 0,
    },
    {
      label: "Unsold Projected",
      id: 'unsold_projected',
      value: unsold_revenue_data.reduce((acc, item) => {
        return acc.plus(item.value);
      }, new Decimal(0)).toNumber(),
      quantity: unsold_revenue_data.reduce((acc, item) => {
        return acc.plus(item.quantity);
      }, new Decimal(0)).toNumber(),
      per_acre: 0,
      per_uom: 0,
    },
    {
      label: "Hedging",
      id: 'heding_gain_loss',
      value: safeParseDecimal({})(base_revenue_data.brokerage?.total?.revenue?.value || 0).toNumber(),
      quantity: safeParseDecimal({})(base_revenue_data.brokerage?.total?.revenue?.quantity || 0).toNumber(),
      per_acre: 0,
      per_uom: 0,
      ignore_quantity_for_cop: true,
    },
  ].map(computeMetaData)

  revenue_data.push(...[{
    label: "cost_of_production",
    id: 'cost_of_production',
    value: safeParseDecimal({})(base_revenue_data?.cost_of_production?.Base?.total_cost).toNumber(),
    quantity: revenue_data.reduce((acc, item) => item.ignore_quantity_for_cop === true ? acc : acc.plus(item.quantity), new Decimal(0)).toNumber(),
    per_acre: 0,
    per_uom: 0,
  }].map(computeMetaData));

  const profit = safeParseDecimal({})(revenue_data[0].value)
      .plus(revenue_data[1].value)
      .plus(revenue_data[2].value)
      .minus(safeParseDecimal({})(revenue_data[3].value));

  const totalQuantity = safeParseDecimal({})(production);


  // NOTE: This is here because the profit and qty use index into the revenue_data
  revenue_data.push(...unsold_revenue_data);

  revenue_data.push({
    label: "Profit",
    id: 'profit',
    value: profit.toNumber(),
    quantity: 0,
    per_acre: 0,
    per_uom: totalQuantity.gt(0) ? profit.div(totalQuantity).toNumber() : 0,
    uom_label: commodity_type === "CNL" ? "lb" : "bu"
  });

  if (base_revenue_data?.unknown?.total?.revenue?.quantity) {
    revenue_data.push({
      label: "Unknown",
      id: 'unknown',
      value: safeParseDecimal({})(base_revenue_data?.unknown?.total?.revenue?.quantity || 0).toNumber(),
      quantity: 0,
      per_acre: 0,
      per_uom: 0,
      uom_label: commodity_type === "CNL" ? "lb" : "bu"
    });
  }

  const revenue_commodities = Object.keys(summaryData.revenue_breakdown).filter(item => item !== 'Overview');

  // Reown stats: Record<ContractType, { contract_type: string; quantity: string }>
  const reown_stats: Record<string, { contract_type: string; quantity: string }> = getPath(summaryData.customer_commodity_reown_stats, [commodity_type, customer_id], {}) as Record<string, { contract_type: string; quantity: string }>;
  const reown_total_quantity = Object.values(reown_stats || {}).reduce((acc, curr) => acc.plus(safeParseDecimal({})(curr.quantity || 0)), new Decimal(0));

  return {
    base_revenue_data,
    revenue_data,
    revenue_commodities,

    percent_hedged_quantity,
    percent_physical_quantity,

    unprotected_futures: unprotectedFutures.toNumber(),
    unprotected_physical: unprotectedPhysical.toNumber(),

    unprotected_futures_percent: unprotectedFuturesPercent.toNumber(),
    unprotected_physical_percent: unprotectedPhysicalPercent.toNumber(),

    futuresBreakdown,
    physicalBreakdown,

    percentOverProjectedProduction: (item: Decimal | number) => calculateProjectPercentage(item).toNumber(),

    percentHedgedWithBeaconBreakdowns,
    percentHedgedWithOutBeaconBreakdowns,

    dpq_basis_percent: calculateProjectPercentage(dpq_basis_quantity).toNumber(),
    dpq_futures_percent: calculateProjectPercentage(dpq_futures_quantity).toNumber(),

    dpq_basis_quantity,
    dpq_futures_quantity,
    dpq_starting_quantity,

    direct_sales_starting_quantity,
    direct_sales_basis_quantity,
    direct_sales_futures_quantity,

    hedged_quantity: hedged_quantity.toNumber(),

    manual_open_offer_stats: manual_open_offer_stats.toNumber(),
    manual_otc_offer_up_stats: manual_otc_offer_up_stats.toNumber(),
    base_open_offer_stats: base_open_offer_stats.toNumber(),

    open_offer_stats: open_offer_stats.toNumber(),

    customer_id,
    commodity_type,
    commodity_display_name: get_groupable_commodity_display_name(commodity_type),

    acreage: safeParseDecimal({})(profile_stats.acreage).toNumber(),
    projected_production: projected_production.toNumber(),
    actual_production: actual_production.toNumber(),

    percent_hedged: percent_hedged.toNumber(),
    percent_hedged_with_dp: percent_hedged_with_dp.toNumber(),

    percent_physical: percent_physical.toNumber(),
    production: production.toNumber(),

    total_yield: total_yield.toNumber(),
    projected_yield: projected_yield.toNumber(),
    actual_yield: actual_yield.toNumber(),

    total_risk_counts: { production_sold: total_production_sold.toNumber(), production_unsold: total_production_unsold.toNumber() },
    breakdown_risk_counts: {
      futures: futures_qty.toNumber(),
      physical: physical_qty.toNumber(),
      unprotected: unprotected_qty.toNumber()
    },

    hedged_without_beacon_quantity: hedged_without_beacon_quantity.toNumber(),
    hedged_without_beacon_percent: hedged_without_beacon_percent.toNumber(),
    hedged_without_beacon_quantity_with_dp: hedged_without_beacon_quantity_with_dp.toNumber(),
    hedged_without_beacon_percent_with_dp: calculateProjectPercentage(hedged_without_beacon_quantity_with_dp).toNumber(),

    cash_quantity: cash_quantity.toNumber(),
    cash_percent: calculateProjectPercentage(cash_quantity).toNumber(),

    hta_quantity: hta_quantity.toNumber(),
    hta_percent: calculateProjectPercentage(hta_quantity).toNumber(),

    basis_quantity: basis_quantity.toNumber(),
    basis_percent: calculateProjectPercentage(basis_quantity).toNumber(),

    beacon_quantity,
    beacon_percent: calculateProjectPercentage(beacon_quantity).toNumber(),

    applicable_dp_quantity: applicable_dp_quantity.toNumber(),
    applicable_dp_percent: calculateProjectPercentage(applicable_dp_quantity).toNumber(),

    non_applicable_dp_quantity: non_applicable_dp_quantity.toNumber(),
    non_applicable_dp_percent: calculateProjectPercentage(non_applicable_dp_quantity).toNumber(),

    unsold_quantity: total_production_unsold.toNumber(),
    unsold_percent: calculateProjectPercentage(total_production_unsold).toNumber(),

    open_brokerage_quantity: open_brokerage_stats.toNumber(),
    open_brokerage_percent: calculateProjectPercentage(open_brokerage_stats).toNumber(),

    non_applicable_open_brokerage_quantity: non_applicable_open_brokerage_stats.toNumber(),
    non_applicable_open_brokerage_percent: calculateProjectPercentage(non_applicable_open_brokerage_stats).toNumber(),

    non_applicable_closed_brokerage_quantity: non_applicable_closed_brokerage_stats.toNumber(),
    non_applicable_closed_brokerage_percent: calculateProjectPercentage(non_applicable_closed_brokerage_stats).toNumber(),

    closed_brokerage_quantity: closed_brokerage_stats.toNumber(),
    closed_brokerage_percent: calculateProjectPercentage(closed_brokerage_stats).toNumber(),

    otc_types: Object.keys(otc_map_stats),
    otc_full_map_stats: otc_map_stats,
    otc_full_quantity: otc_stats.toNumber(),
    otc_full_percent: calculateProjectPercentage(otc_stats).toNumber(),

    double_up_map_stats,
    double_up_quantity,
    double_up_percent: calculateProjectPercentage(double_up_quantity).toNumber(),

    // Note: the basis is an important breakdown and is a subset of the total breakdown (because the futures even if not set is implicit)
    otc_basis_quantity: otc_basis_stats.toNumber(),
    otc_basis_percent: calculateProjectPercentage(otc_basis_stats).toNumber(),

    beacon_basis_quantity: beacon_basis_stats.toNumber(),
    beacon_basis_percent: calculateProjectPercentage(beacon_basis_stats).toNumber(),

    otc_beacon_basis_quantity: otc_beacon_basis_quantity.toNumber(),
    otc_beacon_basis_percent: calculateProjectPercentage(otc_beacon_basis_quantity).toNumber(),

    // The futures is implicit even if not set (so it should match the full quantity)
    otc_futures_quantity: otc_stats.toNumber(),
    otc_futures_percent: calculateProjectPercentage(otc_stats).toNumber(),

    unknown_otc_quantity: unknown_otc_quantity?.toNumber() || 0,
    unknown_otc_percent: calculateProjectPercentage(unknown_otc_quantity).toNumber(),

    reown_stats,
    reown_total_quantity: reown_total_quantity.toNumber(),
  };
}

export function get_groupable_commodity_display_name(base_commodity_type: Commodities) {
  return [Commodities.MinneapolisWheat, Commodities.WinterWheat, Commodities.WinterWheatPrelimCommoditySymbol].includes(base_commodity_type as any) ? 'Wheat' : CommodityOptions.find(option => option.value === base_commodity_type)?.label || base_commodity_type;
}

export function groupable_display_name_to_commodity_list(base_commodity_type: string): Commodities[] {
  if (base_commodity_type === 'Wheat') {
    return [Commodities.MinneapolisWheat, Commodities.WinterWheat, Commodities.WinterWheatPrelimCommoditySymbol];
  }

  return [CommodityOptions.find(option => option.label === base_commodity_type)?.value || base_commodity_type] as Commodities[];
}

export function groupable_display_name_to_drillable_commodity_list(base_commodity_type: string): Commodities[] {
  if (base_commodity_type === 'Wheat') {
    return [Commodities.MinneapolisWheat];
  }

  return [CommodityOptions.find(option => option.label === base_commodity_type)?.value || base_commodity_type] as Commodities[];
}

export function convert_to_groupable_commodity_type(base_commodity_type: Commodities) {
  return [Commodities.MinneapolisWheat, Commodities.WinterWheat, Commodities.WinterWheatPrelimCommoditySymbol].includes(base_commodity_type as any) ? Commodities.MinneapolisWheat : base_commodity_type;
}

export function get_commodity_color(commodity_type: Commodities): string {
  const colorMap: Record<string, string> = {
    [Commodities.Corn]: "bg-yellow-500",
    [Commodities.Soybeans]: "bg-green-600",
    [Commodities.MinneapolisWheat]: "bg-amber-600",
    [Commodities.WinterWheat]: "bg-orange-500",
    [Commodities.WinterWheatPrelimCommoditySymbol]: "bg-orange-500",
    [Commodities.Oats]: "bg-stone-400",
    [Commodities.Canola]: "bg-purple-500",
    [Commodities.PintoBean]: "bg-red-600",
    [Commodities.LiveCattle]: "bg-red-800",
    [Commodities.FeederCattle]: "bg-red-700",
    [Commodities.Barley]: "bg-yellow-600",
    [Commodities.BarleyHulls]: "bg-yellow-700",
    [Commodities.CornScreenings]: "bg-yellow-400",
    [Commodities.Durum]: "bg-orange-600",
    [Commodities.Flax]: "bg-blue-500",
    [Commodities.Flaxseed]: "bg-blue-600",
    [Commodities.GreenPeas]: "bg-green-500",
    [Commodities.Rye]: "bg-brown-500",
    [Commodities.SoybeanHulls]: "bg-green-700",
    [Commodities.SoybeanMeal]: "bg-green-800",
    [Commodities.YellowPeas]: "bg-yellow-500",
    [Commodities.Urea]: "bg-gray-500",
    [Commodities.Sunflowers]: "bg-yellow-400",
    [Commodities.SunflowerScreenings]: "bg-yellow-300",
    [Commodities.PeaAndBeanScreenings]: "bg-green-400",
    [Commodities.WheatMidds]: "bg-orange-400",
    [Commodities.ModifiedDistillersGrains]: "bg-amber-700",
    [Commodities.DriedDistillersGrains]: "bg-amber-800",
    [Commodities.GreenLentils]: "bg-emerald-600",
    [Commodities.CornSilage]: "bg-lime-600",
    [Commodities.MGD]: "bg-slate-600",
  };

  return colorMap[commodity_type] || "bg-gray-400";
}

export function get_commodity_border_color(commodity_type: Commodities): string {
  const borderColorMap: Record<string, string> = {
    [Commodities.Corn]: "border-yellow-600",
    [Commodities.Soybeans]: "border-green-700",
    [Commodities.MinneapolisWheat]: "border-amber-700",
    [Commodities.WinterWheat]: "border-orange-600",
    [Commodities.WinterWheatPrelimCommoditySymbol]: "border-orange-600",
    [Commodities.Oats]: "border-stone-500",
    [Commodities.Canola]: "border-purple-600",
    [Commodities.PintoBean]: "border-red-700",
    [Commodities.LiveCattle]: "border-red-900",
    [Commodities.FeederCattle]: "border-red-800",
    [Commodities.Barley]: "border-yellow-700",
    [Commodities.BarleyHulls]: "border-yellow-800",
    [Commodities.CornScreenings]: "border-yellow-500",
    [Commodities.Durum]: "border-orange-700",
    [Commodities.Flax]: "border-blue-600",
    [Commodities.Flaxseed]: "border-blue-700",
    [Commodities.GreenPeas]: "border-green-600",
    [Commodities.Rye]: "border-brown-600",
    [Commodities.SoybeanHulls]: "border-green-800",
    [Commodities.SoybeanMeal]: "border-green-900",
    [Commodities.YellowPeas]: "border-yellow-600",
    [Commodities.Urea]: "border-gray-600",
    [Commodities.Sunflowers]: "border-yellow-500",
    [Commodities.SunflowerScreenings]: "border-yellow-400",
    [Commodities.PeaAndBeanScreenings]: "border-green-500",
    [Commodities.WheatMidds]: "border-orange-500",
    [Commodities.ModifiedDistillersGrains]: "border-amber-800",
    [Commodities.DriedDistillersGrains]: "border-amber-900",
    [Commodities.GreenLentils]: "border-emerald-700",
    [Commodities.CornSilage]: "border-lime-700",
    [Commodities.MGD]: "border-slate-700",
  };

  return borderColorMap[commodity_type] || "border-gray-500";
}

export function generateDisplayLabelForOtcDetailedType(otc_type: string) {
  if (!otc_type) return undefined;
  if (otc_type === "OTC") return "Unlinked OTC";

  return ContractTypeOptions.find((option) => option.value === otc_type)?.label || otc_type;
}
